import api from './api';

export type AiState = 'BUY_NOW' | 'BUY_ON_PULLBACK' | 'WAIT_FOR_CONFIRMATION' | 'WATCH' | 'AVOID';
export type AiGrade = 'A' | 'A-' | 'B+' | 'B' | 'B-' | 'C' | 'D';
export type AiStatus = 'OK' | 'VALIDATION_FAILED' | 'UNAVAILABLE';

export interface AiAssessment {
  id: number;
  runId: string;
  alertId: number | null;
  symbol: string;
  exchange: string;
  status: AiStatus;
  direction: 'LONG' | 'SHORT' | 'NONE' | null;
  state: AiState | null;
  grade: AiGrade | null;
  conviction: number | null;
  agreesWithSignal: boolean | null;
  summary: string | null;
  mode: 'SHADOW' | 'LIVE';
  model: string;
  promptVersion: string;
  createdAt: string;
  payload?: {
    reasonsFor?: string[];
    reasonsAgainst?: string[];
    redundancyNote?: string;
    /** What the model claims to add that RockBand and the other deterministic signals could not. */
    addsBeyondSignal?: string;
    /** The model's own admission that it is only restating the existing signal. */
    restatesSignal?: boolean;
    keyUncertainty?: string;
    invalidationComment?: string;
    decisionChangingEvent?: string;
    horizonDays?: number;
    volView?: string;
  } | null;
}


export type AiCandidateStage =
  | 'SCANNED' | 'CLUSTER_DUPLICATE' | 'SHORTLISTED'
  | 'ASSESSED' | 'VALIDATION_FAILED' | 'FAILED';

/** One symbol's journey through one nightly run. */
export interface AiCandidate {
  id: number;
  runId: string;
  symbol: string;
  exchange: string;
  stage: AiCandidateStage;
  rockband?: Array<{
    /** Derived from the intervals actually read, e.g. "4H/1D". Never match on this -- use
     *  ctfMinutes. The label follows the data, which is the point of it. */
    pair: string; status: string;
    /** The base timeframe: 240 is the timing pair, 1440 the regime pair. Stable, so it is what
     *  the UI looks up by. */
    ctfMinutes: number;
    /** The higher timeframe actually fetched, or null when the venue serves nothing slower. */
    htf2Minutes: number | null;
    buyCond: boolean | null; shortCond: boolean | null;
    actionFromFlat: string | null;
    bandPosition: number | null; bandWidthPct: number | null;
    ctfKernelAboveHtf: boolean | null; note?: string;
  }> | null;
  evidence?: Record<string, number | null> | null;
  deterministicScore: number | null;
  clusterId: number | null;
  btcBeta: number | null;
  isClusterRep: boolean;
  clusterPeers: string[] | null;
  grade: AiGrade | null;
  state: AiState | null;
  direction: 'LONG' | 'SHORT' | 'NONE' | null;
  conviction: number | null;
  restatesSignal: boolean | null;
  assessment: AiAssessment['payload'] | null;
  summary: string | null;
  finalRank: number | null;
  surfaced: boolean;
  dropReason: string | null;
  humanAction: 'NONE' | 'DISMISSED' | 'WATCHED' | 'TRADED';
  createdAt: string;
}

export interface AiRun {
  id: number;
  runId: string;
  runType: string;
  status: 'PENDING' | 'GATE_FAILED' | 'SUBMITTED' | 'COMPLETED' | 'PARTIAL' | 'FAILED';
  mode: 'SHADOW' | 'LIVE';
  abortReason: string | null;
  dataQuality: any;
  universeSize: number;
  candidateCount: number;
  clusterCount: number;
  shortlistedCount: number;
  surfacedCount: number;
  validationFailures: number;
  model: string | null;
  promptVersion: string | null;
  batchId: string | null;
  tokensIn: number;
  tokensOut: number;
  cacheReadTokens: number;
  cacheCreateTokens: number;
  costUsd: number;
  createdAt: string;
  finishedAt: string | null;
}

export interface AiBoard {
  run: AiRun | null;
  board: AiCandidate[];
  shortlist: AiCandidate[];
  clusters: {
    clusters: Record<string, string[]>;
    betas: Record<string, number | null>;
    threshold: number;
    windowDays: number;
    unclustered: number;
  } | null;
}


export interface AiBucket {
  label: string;
  n: number;
  meanRelPct: number | null;
  medianRelPct: number | null;
  winRate: number | null;
}

/** Shadow-period scoring. Every figure is relative to Bitcoin over the identical window. */
export interface AiScorecard {
  horizonDays: number;
  resolvedCandidates: number;
  /** Signalled, measured, not yet at the horizon. Excludes scans RockBand found nothing on. */
  maturingOutcomes: number;
  /** Signalled but never measured yet — a fresh row legitimately sits here for ~36 hours. */
  pendingOutcomes: number;
  skippedNoAnchor: number;
  byGrade: AiBucket[];
  baseline: AiBucket;
  aiSelected: AiBucket;
  surfaced: AiBucket;
  avoided: AiBucket;
  restatesRate: { n: number; restated: number; pct: number | null };
  contractFailureRate: { assessed: number; failed: number; pct: number | null };
  verdict: string;
  /** Per-bucket progress toward MIN_N, so the headline cannot hide a thin bucket. */
  graduation: Array<{
    label: string; n: number; minN: number; ready: boolean;
    meanRelPct: number | null; winRate: number | null;
  }>;
  /** Raised only when LONG and SHORT genuinely disagree on a sample big enough to matter. */
  directionCaveat: string | null;
}

export interface AiQuantHealth {
  horizon: string;
  state: 'OK' | 'STALE' | 'NO_ACTIVE_MODEL' | 'NONE';
  activeVersion?: number | null;
  activeTrainedAt?: string | null;
  activeAgeDays?: number | null;
  latestVersion?: number | null;
  rejectedSince?: number;
  trainSamples?: number | null;
  upLift?: number | null;
  downLift?: number | null;
  upEce?: number | null;
  latestBlockReason?: string | null;
}

export interface AiBakeoffSide {
  keptN: number;
  meanRelPct: number | null;
  avoidN: number;
  avoidMeanRelPct: number | null;
}

export interface AiBakeoffComparison {
  challengerModel: string;
  horizonDays: number;
  pairedN: number;
  primary: AiBakeoffSide;
  challenger: AiBakeoffSide;
  agreementPct: number | null;
  /** The primary's share of the nightly batches these candidates came from — apportioned, not
   *  summed, because the batch does not bill per candidate. See `costPrimaryBasis`. */
  costPrimaryUsd: number;
  costPrimaryBasis: string;
  costChallengerUsd: number;
  verdict: string;
}

/** One concrete option structure, priced from the live chain. */
export interface AiOptionCandidate {
  ref: string;
  structureId: string;
  name: string;
  direction: string;
  riskShape: 'debit' | 'credit' | 'undefined';
  expiry: string;
  daysToExpiry: number;
  legs: Array<{
    action: string; optionType: string; strike: number; expiry: string;
    quantity: number; ltp: number; bid: number | null; ask: number | null;
  }>;
  netPremium: number;
  marginEstimate: number;
  marginSource?: string;
  maxProfit: number;
  maxLoss: number;
  breakEvens: number[];
  /** From the implied distribution — NOT the suggester's range-fraction. */
  probabilityOfProfit: number | null;
  profitUnbounded: boolean;
  lossUnbounded: boolean;
  payoffAtPlus1Sd: number | null;
  payoffAtMinus1Sd: number | null;
  expectedPayoff: number | null;
  oneSdMovePct: number | null;
  greeks: { delta: number; theta: number; vega: number };
  roiPct: number | null;
  roiPerDayPct: number | null;
  riskReward: number | null;
  thetaPerDayPctOfMargin: number | null;
  breakEvenDistPct: number | null;
  breakEvenInsideExpectedMove: boolean | null;
  fitScore: number;
  warnings: string[];
  liquidityNote: string | null;
}

export interface AiOptionOutlook {
  symbol: string;
  direction: 'LONG' | 'SHORT' | 'NEUTRAL';
  conviction: number;
  volStance: 'SELL_PREMIUM' | 'BUY_PREMIUM' | 'NEUTRAL_VOL';
  volReasoning: string;
  expectedMoveComment: string;
  preferredStructures: string[];
  avoidStructures: string[];
  structureReasoning: string;
  expiryMinDays: number;
  expiryMaxDays: number;
  expiryReasoning: string;
  gammaRiskComment: string;
  portfolioFitComment: string;
  keyRisk: string;
  invalidation: string;
  summary: string;
}

export interface AiOptionRanking {
  ranked: Array<{
    ref: string; rank: number;
    verdict: 'PREFERRED' | 'ACCEPTABLE' | 'REJECT';
    why: string; risks: string[];
  }>;
  noneSuitable: boolean;
  noneSuitableReason: string;
  overallComment: string;
}

export interface AiOptionRecommendation {
  id: number;
  runId: string;
  symbol: string;
  exchange: string;
  status: 'OK' | 'NO_TRADE' | 'NO_DATA' | 'FAILED';
  note: string | null;
  outlook: AiOptionOutlook | null;
  marketContext: any;
  exposure: {
    openPositions: number; shortOptionLegs: number; longOptionLegs: number;
    strategies: string[]; nearestExpiry: string | null;
  } | null;
  candidates: AiOptionCandidate[] | null;
  ranking: AiOptionRanking | null;
  topRef: string | null;
  topStructure: string | null;
  topExpiry: string | null;
  topRoiPct: number | null;
  topRoiPerDayPct: number | null;
  topRiskReward: number | null;
  topWinProbability: number | null;
  model: string | null;
  promptVersion: string | null;
  costUsd: number;
  humanAction: 'NONE' | 'DISMISSED' | 'WATCHED' | 'BUILT';
  createdAt: string;
}

/** States a POSITION review can return — a different question, so a different vocabulary. */
export type AiPositionState =
  | 'HOLD' | 'TRIM' | 'EXIT_NOW' | 'EXIT_ON_STRENGTH' | 'ADD' | 'NO_VIEW';

/** The open position on one symbol, as the review sees it. */
export interface AiPositionContext {
  symbol: string;
  exchange: string;
  side: 'LONG' | 'SHORT';
  quantity: number;
  averageEntryPrice: number;
  currentPrice: number | null;
  /** Signed for the side — positive means the position is winning. */
  openPnlPct: number | null;
  unrealisedPnl: number | null;
  daysHeld: number;
  status: string;
  legCount: number;
  openedAt: string;
}

export type AiMaturity = 'COMPLETE' | 'MATURING' | 'PENDING' | 'UNSCOREABLE';

/**
 * One alert's realised outcome.
 *
 * `bestPct` and `worstPct` are signed for the direction that was PROPOSED — a short that fell
 * shows a gain. They are not the raw price excursion, and the raw excursion is deliberately not
 * exposed here: reading it as "max return" was wrong for two thirds of the recorded candidates.
 */
export interface AiPerformanceRow {
  id: number;
  runId: string;
  createdAt: string;
  symbol: string;
  exchange: string;
  direction: 'LONG' | 'SHORT' | null;
  grade: AiGrade | null;
  state: AiState | null;
  stage: AiCandidateStage;
  conviction: number | null;
  surfaced: boolean;
  humanAction: string;
  restatesSignal: boolean | null;
  deterministicScore: number | null;
  anchorPrice: number | null;
  bestPct: number | null;
  worstPct: number | null;
  bestHighPct: number | null;
  worstLowPct: number | null;
  daysToBest: number | null;
  relPct: number | null;
  retPct: number | null;
  benchRetPct: number | null;
  barsAvailable: number;
  maturity: AiMaturity;
  note: string | null;
  seenTimes: number;
}

export interface AiPerformanceBucket {
  label: string;
  /** Rows with ANY measurement. Horizon-independent — it does not move with the 1d/5d/10d toggle. */
  n: number;
  /** Rows resolved AT THE SELECTED HORIZON — the denominator behind meanRelPct and winRate. */
  nRel: number;
  meanBestPct: number | null;
  medianBestPct: number | null;
  meanWorstPct: number | null;
  winRate: number | null;
  meanRelPct: number | null;
  medianDaysToBest: number | null;
}

export interface AiPerformanceReport {
  horizonDays: number;
  rows: AiPerformanceRow[];
  totalBeforeGrouping: number;
  byGrade: AiPerformanceBucket[];
  byDirection: AiPerformanceBucket[];
  byState: AiPerformanceBucket[];
  counts: Record<AiMaturity, number>;
  /** Horizons with at least one scoreable row, longest first. */
  horizonsWithData: number[];
  minSampleForVerdict: number;
  /** One computed reading of what the engine is doing, with the sample size behind each claim. */
  insight: {
    headline: string;
    findings: Array<{
      kind: 'GRADE' | 'DIRECTION' | 'CALL';
      label: string; n: number; meanRelPct: number | null; winRate: number | null;
    }>;
    belowSample: Array<{ kind: string; label: string; n: number }>;
    minSample: number;
    horizonDays: number;
    computedAt: string;
  };
}

/** One stored AI view of a symbol, whichever table it came from. */
export interface AiSymbolView {
  symbol: string;
  grade: string | null;
  state: string | null;
  direction: string | null;
  conviction: number | null;
  summary: string | null;
  /** When the view was formed — so a row can disclose that it is last night's, not this signal's. */
  at: string;
  source: 'ASSESSMENT' | 'NIGHTLY_BOARD';
}

export interface AiCostReport {
  perStage: Array<{
    stage: string; medianUsd: number | null; p90Usd: number | null;
    meanUsd: number | null; n: number; sinceDays: number;
  }>;
  /** What one press of each billed control costs, measured from the ledger. */
  perAction: { askAi: number | null; nightlyRun: number | null; optionDesk: number | null };
  spend: {
    todayUsd: number; last7Usd: number; last30Usd: number;
    byDay: Array<{ day: string; costUsd: number; calls: number }>;
    byMonth: Array<{ month: string; costUsd: number; calls: number }>;
  };
  efficiency: {
    perEvaluatedCandidateUsd: number | null;
    perActionableDecisionUsd: number | null;
    evaluatedCandidates: number;
    actionableDecisions: number;
    windowDays: number;
  };
  guard: { enabled: boolean; capUsd: number; spentTodayUsd: number; capReached: boolean };
  model: {
    selected: string | null;
    price: { inPerM: number; outPerM: number } | null;
    priceEstimated: boolean;
    priceBasis: string | null;
    tierNote: string | null;
    ceiling: string | null;
  };
  notes: string[];
}

export interface AiModelCatalogue {
  available: boolean;
  reason: string | null;
  selected: string | null;
  selectedReason?: string | null;
  eligible: Array<{
    id: string; displayName: string; family: string | null; version: number;
    /** True when the tier ceiling keeps AUTOMATIC selection off this one. Still pinnable by hand. */
    aboveCeiling?: boolean;
    price: { inPerM: number; outPerM: number };
  }>;
  rejected: Array<{ id: string; reason: string }>;
  ceiling: string | null;
  tierNote: string | null;
}

/** Per-domain wrapper over the shared axios client, matching the other services/ modules. */
const aiService = {

  /** Is there an open position on this symbol? Free and read-only — decides which question to ask. */
  async getPositionContext(symbol: string, exchange: string): Promise<AiPositionContext | null> {
    const r = await api.get(
      `/ai/position/${encodeURIComponent(symbol)}?exchange=${encodeURIComponent(exchange)}`);
    return ((r.data as any)?.data ?? null);
  },

  /**
   * "Should I still be holding this?" — a review of an OPEN position.
   *
   * A different question from assessNow, with its own strict schema and state vocabulary. Returns
   * `noPosition` rather than throwing when the book is flat: that is a legitimate answer.
   */
  async reviewPosition(symbol: string, exchange: string):
    Promise<{ assessment: AiAssessment | null; noPosition: boolean }> {
    const r = await api.post('/ai/review-position', { symbol, exchange });
    const d = (r.data as any)?.data ?? {};
    return { assessment: d.assessment ?? null, noPosition: !!d.noPosition };
  },

  /** Per-alert history: date, direction, best and worst the position ever got to. */
  async getPerformance(params: {
    horizon?: 1 | 5 | 10; sinceDays?: number; grade?: string; state?: string;
    direction?: string; symbol?: string; minBars?: number; surfacedOnly?: boolean;
  } = {}): Promise<AiPerformanceReport | null> {
    const qs = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') qs.set(k, String(v));
    });
    const r = await api.get(`/ai/performance?${qs.toString()}`);
    return ((r.data as any)?.data ?? null);
  },

  /**
   * The same rows the table shows, as a CSV download.
   *
   * It takes the FULL filter set, not just the horizon. It used to accept only `{horizon,
   * sinceDays}`, so a reader who had narrowed the table to SHORT grade-C rows and pressed CSV got
   * a file of everything — an export that cannot be reconciled against the screen it came from is
   * worse than no export, because the mismatch is invisible until someone acts on it.
   */
  performanceCsvUrl(params: {
    horizon?: 1 | 5 | 10; sinceDays?: number; grade?: string; state?: string;
    direction?: string; symbol?: string; minBars?: number; surfacedOnly?: boolean;
  } = {}): string {
    const qs = new URLSearchParams({ format: 'csv' });
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '' && v !== false) qs.set(k, String(v));
    });
    return `/ai/performance?${qs.toString()}`;
  },

  /**
   * The latest AI view per symbol, keyed by UPPERCASE symbol, from the board AND the per-signal
   * table. One request for a whole grid.
   *
   * Read-only by construction: it renders what is stored and never triggers an assessment, which
   * is the same rule that keeps AiVerdictChip click-to-fetch. A grid that assessed itself would
   * spend money by being looked at.
   */
  async getSymbolViews(hours = 72): Promise<Record<string, AiSymbolView>> {
    const r = await api.get(`/ai/symbol-views?hours=${hours}`);
    return ((r.data as any)?.data ?? {}) as Record<string, AiSymbolView>;
  },

  /** Measured spend. Feeds the cost hint on every billed control and the Dashboard panel. */
  async getCost(): Promise<AiCostReport | null> {
    const r = await api.get('/ai/cost');
    return ((r.data as any)?.data ?? null);
  },

  /** What this account can reach, what was auto-selected, and what was passed over. */
  async getModels(refresh = false): Promise<AiModelCatalogue | null> {
    const r = await api.get(`/ai/models${refresh ? '?refresh=true' : ''}`);
    return ((r.data as any)?.data ?? null);
  },

  /**
   * The AI's current view of one symbol, from whichever path produced it most recently — the
   * nightly board or a one-off ask. Reading only one table would miss most verdicts.
   */
  async getSymbolView(symbol: string, hours = 72): Promise<(AiAssessment & { source?: string; surfaced?: boolean; finalRank?: number | null }) | null> {
    const r = await api.get(`/ai/symbol/${encodeURIComponent(symbol)}?hours=${hours}`);
    return ((r.data as any)?.data ?? null);
  },

  /**
   * Run the Option Desk for one underlying. Synchronous and slow — two model calls at high effort —
   * so the caller should show progress rather than assume it returns quickly.
   */
  async runOptionDesk(body: { symbol: string; riskInr?: number; availableMarginInr?: number; dryRun?: boolean }): Promise<any> {
    const r = await api.post('/ai/options/run', body);
    return (r.data as any)?.data ?? null;
  },

  /** Latest recommendation per underlying, or one symbol's. */
  async getOptionRecommendations(symbol?: string): Promise<AiOptionRecommendation[] | AiOptionRecommendation | null> {
    const r = await api.get(`/ai/options/recommendations${symbol ? `?symbol=${encodeURIComponent(symbol)}` : ''}`);
    return (r.data as any)?.data ?? null;
  },

  async setOptionRecommendationAction(id: number, action: 'NONE' | 'DISMISSED' | 'WATCHED' | 'BUILT'): Promise<AiOptionRecommendation> {
    const r = await api.post(`/ai/options/recommendations/${id}/action`, { action });
    return (r.data as any)?.data as AiOptionRecommendation;
  },

  /** Model bake-off: primary vs challenger on the same candidates and the same outcomes. */
  async getBakeoff(horizonDays: 1 | 5 | 10 = 5): Promise<AiBakeoffComparison | null> {
    const r = await api.get(`/ai/bakeoff?horizon=${horizonDays}`);
    return ((r.data as any)?.data ?? null) as AiBakeoffComparison | null;
  },

  /** Quant-ranker health — surfaces a model frozen because newer ones keep being rejected. */
  async getQuantHealth(): Promise<AiQuantHealth[]> {
    const r = await api.get('/ai/quant-health');
    return ((r.data as any)?.data ?? []) as AiQuantHealth[];
  },

  /** Shadow-period scorecard. Resolves any newly-mature outcomes server-side first. */
  async getScorecard(horizonDays: 1 | 5 | 10 = 5): Promise<AiScorecard | null> {
    const r = await api.get(`/ai/scorecard?horizon=${horizonDays}`);
    return ((r.data as any)?.data ?? null) as AiScorecard | null;
  },

  /** The current board: the latest run that produced candidates, with its header and rows. */
  async getBoard(): Promise<AiBoard> {
    const r = await api.get('/ai/board');
    return ((r.data as any)?.data ?? { run: null, board: [], shortlist: [], clusters: null }) as AiBoard;
  },

  async getRuns(): Promise<AiRun[]> {
    const r = await api.get('/ai/runs');
    return ((r.data as any)?.data ?? []) as AiRun[];
  },

  /** Every row of one run, including the ones that never reached the model. */
  async getCandidates(runId: string): Promise<AiCandidate[]> {
    const r = await api.get(`/ai/candidates?runId=${encodeURIComponent(runId)}`);
    return ((r.data as any)?.data ?? []) as AiCandidate[];
  },

  /**
   * Start a nightly run. Returns as soon as it is dispatched — a full pass takes minutes, so
   * progress is read from getRuns() rather than held open on this request.
   */
  async runNightly(opts?: { dryRun?: boolean; limit?: number }): Promise<{ started: boolean }> {
    const r = await api.post('/ai/runs/nightly', opts || {});
    return ((r.data as any)?.data ?? { started: false });
  },

  /** Record what the user did with an idea — the only mutable field on a candidate. */
  async setCandidateAction(id: number, action: 'NONE' | 'DISMISSED' | 'WATCHED' | 'TRADED'): Promise<AiCandidate> {
    const r = await api.post(`/ai/candidates/${id}/action`, { action });
    return (r.data as any)?.data as AiCandidate;
  },

  async getConfig(): Promise<any> {
    const r = await api.get('/ai/config');
    return (r.data as any)?.data ?? null;
  },

  /** Latest assessment per symbol for this user. */
  async getAssessments(hours = 48): Promise<AiAssessment[]> {
    const r = await api.get(`/ai/assessments?hours=${hours}`);
    return ((r.data as any)?.data ?? []) as AiAssessment[];
  },

  async getForAlert(alertId: number): Promise<AiAssessment[]> {
    const r = await api.get(`/ai/assessments/alert/${alertId}`);
    return ((r.data as any)?.data ?? []) as AiAssessment[];
  },

  /** Assess a symbol on demand, outside the alert flow. */
  /** Prefer passing { alertId } — the server resolves symbol + exchange from the alert's
   *  instrument, which the dashboard payload does not carry. */
  async assessNow(body: { alertId?: number; symbol?: string; exchange?: string; action?: string; timeframe?: number | string; source?: string }): Promise<AiAssessment | null> {
    const r = await api.post('/ai/assess', body);
    return ((r.data as any)?.data ?? null) as AiAssessment | null;
  },
};

export default aiService;
